import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { omit } from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { ReactNode, useState } from "react";

import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { ChoosePencilButton } from "../../../components/ui-kit/tables/cells/cell-choose";
import { createPatch } from "../../../utils/patch";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PatientsModal } from "../common/patients-modal";
import { PhysiciansModal } from "../common/physicians-modal";
import { AccessPoliciesItem } from "./access-policies";
import { CellLocations } from "./cell-locations";
import { PermissionsModal } from "./permissions-modal";
import {
  getCountriesFromLocations,
  getRegionsFromLocations,
  Location,
} from "./utils";

type ChooserKey =
  | "permissions"
  | "locations"
  | "clinics"
  | "physicians"
  | "visible_patients";

interface Fields {
  monitoring_active: boolean;
  permissions: number[];
  locations: Location[];
  clinic_uuids: string[];
  physician_uuids: string[];
  visible_patient_ids: number[];
}

interface ChooserConfig {
  key: ChooserKey;
  label: string;
  count: () => string;
  renderModal: (props: { onClose: () => void }) => ReactNode;
}

export const EditAccessPolicyDialog = observer(
  function EditAccessPolicyDialog(props: {
    item: AccessPoliciesItem;
    admin: AdminConsole;
    onClose: () => void;
    onSave: (patch: Record<string, unknown>) => Promise<void>;
  }) {
    const { item, admin, onClose, onSave } = props;
    const { t } = useLingui();

    const [{ initial, fields }] = useState(() => {
      const init = buildInitialFields(item);
      return { initial: init, fields: observable<Fields>({ ...init }) };
    });

    const [openChooser, setOpenChooser] = useState<ChooserKey | null>(null);
    const closeChooser = () => setOpenChooser(null);

    const choosers: ChooserConfig[] = [
      {
        key: "permissions",
        label: t`Permissions`,
        count: () => t`${fields.permissions.length} chosen`,
        renderModal: ({ onClose }) => (
          <PermissionsModal
            title={t`Edit permissions for: ${item.username}`}
            preselected={fields.permissions}
            onClose={onClose}
            onConfirm={({ selected }) => {
              fields.permissions = selected;
            }}
          />
        ),
      },
      {
        key: "locations",
        label: t`Locations`,
        count: () =>
          plural(fields.locations.length, {
            one: "# country",
            other: "# countries",
          }),
        renderModal: ({ onClose }) => (
          <CellLocations item={item} patch={fields} onClose={onClose} />
        ),
      },
      {
        key: "clinics",
        label: t`Clinics`,
        count: () => t`${fields.clinic_uuids.length} chosen`,
        renderModal: ({ onClose }) => (
          <ClinicsModal
            title={t`Edit clinics for: ${item.username}`}
            preselected={fields.clinic_uuids}
            mode="multi"
            model={admin.createClinics()}
            onClose={onClose}
            onConfirm={({ selected }) => {
              fields.clinic_uuids = selected;
            }}
          />
        ),
      },
      {
        key: "physicians",
        label: t`Physicians`,
        count: () => t`${fields.physician_uuids.length} chosen`,
        renderModal: ({ onClose }) => (
          <PhysiciansModal
            title={t`Edit physicians for: ${item.username}`}
            preselected={fields.physician_uuids}
            mode="multi"
            model={admin.createPhysicians()}
            onClose={onClose}
            onConfirm={({ selected }) => {
              fields.physician_uuids = selected;
            }}
          />
        ),
      },
      {
        key: "visible_patients",
        label: t`Visible patients`,
        count: () => t`${fields.visible_patient_ids.length} chosen`,
        renderModal: ({ onClose }) => (
          <PatientsModal
            title={t`Edit visible patients for: ${item.username}`}
            preselected={fields.visible_patient_ids}
            mode="multi"
            model={admin.createPatients()}
            onClose={onClose}
            onConfirm={({ selected }) => {
              fields.visible_patient_ids = selected;
            }}
          />
        ),
      },
    ];

    const activeChooser = choosers.find((c) => c.key === openChooser);

    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <h3 className="mb-6 text-base font-bold">
          <Trans>Edit access policy for: {item.username}</Trans>
        </h3>

        <Form
          onSubmit={async () => {
            const patch = createPatch(
              fields as unknown as Record<string, unknown>,
              initial as unknown as Record<string, unknown>,
            );
            if (Object.keys(patch).length === 0) {
              onClose();
              return;
            }
            const body: Record<string, unknown> = omit(patch, "locations");
            if ("locations" in patch) {
              body.countries = getCountriesFromLocations(fields.locations);
              body.regions = getRegionsFromLocations(fields.locations);
            }
            await onSave(body);
            onClose();
          }}
        >
          <FormTable dataAdd>
            <FormRow
              label={t`Monitoring active`}
              data={<Checkbox model={fields} attr="monitoring_active" />}
            />

            {choosers.map((c) => (
              <FormRow
                key={c.key}
                label={c.label}
                data={
                  <ChoosePencilButton
                    label={c.count()}
                    onClick={() => setOpenChooser(c.key)}
                  />
                }
              />
            ))}
          </FormTable>

          <div className="mt-6 flex justify-center gap-2">
            <Button type="submit">
              <Trans>Save</Trans>
            </Button>
            <Button
              type="button"
              variant="subtle"
              size="default"
              onClick={onClose}
            >
              <Trans>Cancel</Trans>
            </Button>
          </div>
        </Form>

        {activeChooser?.renderModal({ onClose: closeChooser })}
      </Dialog>
    );
  },
);

function buildInitialFields(item: AccessPoliciesItem): Fields {
  return {
    monitoring_active: item.monitoring_active ?? false,
    permissions: [...(item.permissions ?? [])],
    locations: [...(item.locations ?? [])],
    clinic_uuids: (item.clinics ?? []).map((c) => c.uuid),
    physician_uuids: (item.physicians ?? []).map((p) => p.uuid),
    visible_patient_ids: (item.visible_patients ?? []).map((p) => p.patient_id),
  };
}
